
import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Footer } from '@/components/Footer';
import { SEOHead } from '@/components/SEOHead';
import { CheckCircle, Crown, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { getErrorMessage } from '@/lib/errorMessage';

const Onboarding = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const sessionId = searchParams.get('session_id');

  // This page used to prefill the email from get-session-email. That function
  // now answers only donation checkouts (ERI-2782), and no checkout has sent
  // anyone here since Aug 2025.

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        title: 'Password Mismatch',
        description: 'Passwords do not match. Please try again.',
        variant: 'destructive',
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: 'Password Too Short',
        description: 'Password must be at least 6 characters long.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      // Complete the user setup with the password
      const { data } = await supabase.functions.invoke('complete-user-setup', {
        body: { 
          sessionId,
          password 
        }
      });

      if (data?.success) {
        toast({
          title: 'Account Setup Complete!',
          description: 'Your premium account is ready. You can now sign in.',
        });
        
        // Redirect to main app with success message
        navigate('/?onboarding=complete');
      } else {
        throw new Error(data?.error || 'Failed to complete setup');
      }
    } catch (error) {
      console.error('Error setting up account:', error);
      toast({
        title: 'Setup Failed',
        description: getErrorMessage(error) || 'Failed to complete account setup. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Set Up Your Account | Milky Way Planner"
        description="Complete your Milky Way Planner Premium account setup. Create your password and start planning astrophotography sessions."
        noindex
      />
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-stone-50 to-amber-50/30">
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          {/* ... keep existing code */}
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <Crown className="w-8 h-8 text-yellow-600" />
            </div>
            <h1 className="text-2xl font-semibold leading-none tracking-tight text-green-700">Welcome to Premium!</h1>
            <p className="text-sm text-gray-600 mt-2">
              Your payment was successful. Now let's set up your account.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSetPassword} className="space-y-4">
              <div>
                <Label htmlFor="password">Set Your Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    placeholder="Enter your password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="Confirm your password"
                />
              </div>
              
              <Button type="submit" disabled={loading} className="w-full" size="lg">
                {loading ? 'Setting up your account...' : 'Complete Account Setup'}
              </Button>
            </form>
            
            <div className="mt-4 text-center text-sm text-gray-500">
              <CheckCircle className="w-4 h-4 inline mr-1" />
              Premium features unlocked
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
    </>
  );
};

export default Onboarding;
